import { FC, useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { criteriaAnswers } from "../../../helpers/criteriaAnswers";
import { CsiAnswer } from "../../../types/csiAnswer";
import { useActions } from "../../../hooks/useActions";
import { getLimitedCriteriaAnswers, getNumberOfOptions } from "../../../helpers/getLimitedCriteriaAnswers";
import MarkdownParser from "../../../components/markdown";

const LimitedRangingComponent: FC = () => {
  const { question, appLang, answer } = useSelector((state: RootState) => ({
    question: state.questions.question,
    answer: state.questions.answer,
    appLang: state.utils.lang,
  }));

  const [options, setOptions] = useState(getNumberOfOptions());
  const [answers, setAnswers] = useState<Array<CsiAnswer>>(getLimitedCriteriaAnswers());
  const [selectedValues, setSelectedValues] = useState<number[]>([]);
  const { setUserAnswer } = useActions();

  useEffect(() => {
    setOptions(getNumberOfOptions());
    setAnswers(getLimitedCriteriaAnswers());
    setSelectedValues([]);
    setUserAnswer({
      answerValue: undefined,
      csiAnswers: criteriaAnswers(),
    });
  }, [appLang, question?.questionId]);

  const getAvailableOptions = useCallback(
    (currentValue?: number) => {
      const baseOptions = options.filter(
        (option) => !selectedValues.includes(option.value) || option.value === currentValue,
      );

      return [
        {
          label: <span style={{ color: "#888" }}> – </span>,
          value: 0,
          disabled: currentValue === undefined,
        },
        ...baseOptions,
      ];
    },
    [options, selectedValues],
  );

  const onSelect = useCallback(
    (score: number, criteriaId: string, oldValue?: number) => {
      if (!criteriaId) return;

      if (score === 0) {
        setSelectedValues((prev) => prev.filter((v) => v !== oldValue));

        const csiAnswers = answers.map((answer$) => ({
          ...answer$,
          criterias: answer$.criterias.map((criteria$) =>
            String(criteria$.id) !== criteriaId ? criteria$ : { ...criteria$, score: undefined },
          ),
        }));

        setAnswers(csiAnswers);
        setUserAnswer({ ...answer, csiAnswers });
        return;
      }

      setSelectedValues((prev) => {
        const newValues = prev.filter((v) => v !== oldValue);
        newValues.push(score);
        return newValues;
      });

      const csiAnswers = answers.map((answer$) => ({
        ...answer$,
        criterias: answer$.criterias.map((criteria$) =>
          String(criteria$.id) !== criteriaId ? criteria$ : { ...criteria$, score },
        ),
      }));

      setAnswers(csiAnswers);
      setUserAnswer({ ...answer, csiAnswers });
    },
    [answers, answer, setUserAnswer],
  );

  return (
    <div className="csi-template">
      {answers.map((csi, i) => (
        <div key={i} className="csi-item">
          <h2
            className={question?.surveyDecor?.textFont ? "disable-global-font" : ""}
            style={{ fontFamily: question?.surveyDecor?.textFont }}
          >
            {csi.paramName}
          </h2>
          <div className="criteria-container">
            {csi.criterias.map((criteria) => (
              <div className="criteria-item" key={criteria.id} id={String(criteria.id)}>
                <h3
                  className={`criteria-item ${question?.surveyDecor?.textFont ? "disable-global-font" : ""}`}
                  style={{ fontFamily: question?.surveyDecor?.textFont }}
                >
                  <MarkdownParser mixedString={criteria.name} />
                </h3>
                <Select
                  onChange={(value) => onSelect(value, String(criteria.id), criteria.score)}
                  className="criteria-select"
                  value={criteria.score === 0 ? undefined : criteria.score}
                  variant="borderless"
                  placeholder=". . ."
                  notFoundContent="----"
                  options={getAvailableOptions(criteria.score)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LimitedRangingComponent;
